import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { App } from '@df/core/app';
import { NCore } from '../../df-core.interfaces';

/**
 * Full-reload-on-stale-tab.
 *
 * Browser-only service that watches router navigations. Each completed
 * navigation refreshes an "idle" timer; when a navigation starts after the tab
 * has been idle past the configured `staleTabReload.maxIdleMs`, the in-app
 * navigation is handed to the browser as a normal full page request, so the
 * document (and its app bundle) is loaded fresh from the server.
 *
 * Disabled unless `staleTabReload.maxIdleMs` is set to a positive value via
 * {@link provideDfCore}.
 */
@Injectable({ providedIn: 'root' })
export class StaleTabReloadService {
    private readonly router = inject(Router);
    private readonly app = inject(App);
    private readonly document = inject<Document>(DOCUMENT);

    /**
     * Once the SPA has been open this long (ms) without an in-app navigation,
     * the next user-initiated navigation does a full document load instead of an
     * Angular route change. `0` disables the behaviour.
     */
    private readonly maxIdleMs = inject(NCore.OPTIONS).staleTabReload?.maxIdleMs ?? 0;

    /** Timestamp of the last completed navigation; seeded with the initial load. */
    private lastNavigatedAt = Date.now();

    appInitialize(): void {
        // Idle/reload only makes sense in the browser (SSR has no long-lived tab)
        // and when a positive idle threshold has been configured.
        if (!this.app.isPlatformBrowser || this.maxIdleMs <= 0) {
            return;
        }

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.lastNavigatedAt = Date.now();
                return;
            }

            if (!(event instanceof NavigationStart)) {
                return;
            }

            if (
                (event.navigationTrigger === 'imperative' || event.navigationTrigger === 'popstate') &&
                Date.now() - this.lastNavigatedAt > this.maxIdleMs
            ) {
                // Hand the navigation to the browser: this supersedes the SPA route
                // change and loads the target URL as a normal full page request.
                this.document.location.href = event.url;
                return;
            }

            // Any navigation we let proceed counts as activity, so an internal
            // redirect it spawns (error-handler RedirectCommand, catalog URL
            // rewrite) — which is itself 'imperative' — is not mistaken for a
            // stale-tab click and does not trigger a spurious full reload.
            this.lastNavigatedAt = Date.now();
        });
    }
}
